import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useOidcUser } from '@axa-fr/react-oidc';
import getAuthClient from 'Tasking_Helpers/getAuthClient';
import FedExAddressVerificationService from './services/FedExAddressVerificationService';
import {
  StyledContainer,
  AddressSection,
  Form,
  StyledFormGroup,
  StyledLabel,
  StyledInput,
  ErrorText,
  StyledLoadingButton,
  SuccessMessage,
  ErrorMessage,
  SectionTitle,
  OptInSection,
  OptInCheckbox,
  CancelButton,
  CareGapsSection,
} from './foodBoxActivityStyles';

const FoodBoxActivity = ({
  memberInfo,
  ngm,
  setActions,
  actions,
  setCurrentTab,
  setActionToDisplay,
  values,
  setValues,
}) => {
  const { oidcUser } = useOidcUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [showAddressSuggestion, setShowAddressSuggestion] = useState(false);
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [submittingOptIn, setSubmittingOptIn] = useState(false);
  const [loadingOptInStatus, setLoadingOptInStatus] = useState(true);
  const [selectedFoodBoxType, setSelectedFoodBoxType] = useState('');
  const [careGaps, setCareGaps] = useState([]);
  const [loadingCareGaps, setLoadingCareGaps] = useState(true);
  const [savingCareGap, setSavingCareGap] = useState(null);
  const [optInTypeInfo, setOptInTypeInfo] = useState({ type: '', name: '' });

  // Get current user ID from OIDC token
  const currentUserId = oidcUser?.sub || 0;

  // Food box options for special groups
  const foodBoxOptions = [
    { value: '', label: 'Select Food Box Type' },
    { value: 'mp', label: 'MP, MP' },
    { value: 'mp_pantry', label: 'MP, MP + Pantry' },
    { value: 'mp_pantry_protein', label: 'MP, MP + Pantry + Protein' },
  ];

  // Check if member is in special group (9501) or network (9500)
  const isSpecialGroup = memberInfo?.groupId === 9501 || memberInfo?.networkId === 9500;
  
  // Determine if we should show the dropdown
  // Show dropdown only if: special group AND not opted in yet
  const showDropdown = isSpecialGroup && !isOptedIn;

  const fedexService = new FedExAddressVerificationService();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      streetAddress: memberInfo?.address || '',
      city: memberInfo?.city || '',
      state: memberInfo?.state || '',
      zipCode: memberInfo?.zip || '',
    },
  });

  useEffect(() => {
    fetchOptInStatus();
    fetchCareGaps();
  }, [memberInfo]);

  const fetchOptInStatus = async () => {
    try {
      setLoadingOptInStatus(true);
      const authorizedApi = await getAuthClient();
      const params = {
        organizationId: memberInfo?.organizationId || memberInfo?.activeOrganizationId || 1,
        ...(ngm ? { ngmId: memberInfo?.id } : { userId: memberInfo?.userId }),
      };

      const response = await authorizedApi.get(`${process.env.API_BASE_URL}/v1/foodbox/optin/status`, { params });

      if (response.data.hasRecord) {
        setIsOptedIn(response.data.optIn);
        setSelectedFoodBoxType(response.data.foodBoxType || '');
        setOptInTypeInfo({
          type: response.data.optInType || '',
          name: response.data.optInName || '',
        });
      }
    } catch (error) {
      // Don't show error to user, just default to unchecked
    } finally {
      setLoadingOptInStatus(false);
    }
  };

  const fetchCareGaps = async () => {
    try {
      setLoadingCareGaps(true);
      const authorizedApi = await getAuthClient();
      const params = {
        organizationId: memberInfo?.organizationId || memberInfo?.activeOrganizationId || 1,
        networkGroupMemberId: ngm ? memberInfo?.id : memberInfo?.networkGroupMemberId,
      };
      
      const response = await authorizedApi.get(`${process.env.API_BASE_URL}/v1/foodbox/caregaps`, { params });
      setCareGaps(response.data || []);
    } catch (error) {
      console.error('Failed to fetch care gaps:', error);
      setCareGaps([]);
    } finally {
      setLoadingCareGaps(false);
    }
  };

  const handleVerifyAddress = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setVerificationResult(null);
      setShowAddressSuggestion(false);

      // Verify address with FedEx
      const verificationResponse = await fedexService.verifyAddress({
        streetAddress: data.streetAddress,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      });

      setVerificationResult(verificationResponse);

      if (!verificationResponse.isValid) {
        setSubmitError(verificationResponse.error || 'Address verification failed');
        return;
      }

      // Show suggestion popup with results
      setShowAddressSuggestion(true);

      // Don't set error message here - we'll show it in the suggestion box with proper styling
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'Failed to verify address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAddress = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Save current form values
      await updateMemberAddress({
        streetAddress: data.streetAddress,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
      });

      setSubmitSuccess(true);
      setShowAddressSuggestion(false);
      setVerificationResult(null);
      setActions({ ...actions, addressUpdated: true });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      setSubmitError(error.message || 'Failed to save address');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateMemberAddress = async (addressData) => {
    const authorizedApi = await getAuthClient();
    const addressPatchItems = {
      address: addressData.streetAddress,
      city: addressData.city,
      state: addressData.state,
      zip: addressData.zipCode,
    };

    try {
      if (ngm) {
        // Update NGM address
        const response = await authorizedApi.patch(
          `${process.env.API_BASE_URL}/v5/ngms/${memberInfo.organizationId}:${memberInfo.id}`,
          { ngmPatchItems: addressPatchItems },
        );
        return response.data;
      } else {
        // Update Member address
        const response = await authorizedApi.patch(`${process.env.API_BASE_URL}/v5/members/${memberInfo.userId}`, {
          memberPatchItems: addressPatchItems,
        });
        return response.data;
      }
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update address in system');
    }
  };

  const handleOptInToggle = async () => {
    // If special group and not opted in, just toggle the checkbox without saving
    // User will need to select type and click submit button to save
    if (showDropdown) {
      setIsOptedIn(!isOptedIn);
      return;
    }

    // For non-special groups, save immediately (toggle behavior)
    try {
      setSubmittingOptIn(true);
      const authorizedApi = await getAuthClient();
      const newOptInStatus = !isOptedIn;

      const payload = {
        organizationId: memberInfo?.organizationId || memberInfo?.activeOrganizationId || 1,
        ...(ngm ? { ngmId: memberInfo.id } : { userId: memberInfo.userId }),
        foodBoxType: null,
        optIn: newOptInStatus,
        optInType: null,
        optInName: null,
      };

      await authorizedApi.put(`${process.env.API_BASE_URL}/v1/foodbox/optin`, payload);

      setIsOptedIn(newOptInStatus);
      setActions({ ...actions, optInUpdated: true });
    } catch (error) {
      alert('Failed to update food box opt-in. Please try again.');
    } finally {
      setSubmittingOptIn(false);
    }
  };

  const handleFoodBoxTypeChange = (event) => {
    const newType = event.target.value;
    setSelectedFoodBoxType(newType);
  };

  const handleSubmitOptIn = async () => {
    if (!selectedFoodBoxType) {
      alert('Please select a food box type.');
      return;
    }

    try {
      setSubmittingOptIn(true);
      const authorizedApi = await getAuthClient();

      // Find the selected option to get both code and name
      const selectedOption = foodBoxOptions.find((opt) => opt.value === selectedFoodBoxType);

      const payload = {
        organizationId: memberInfo?.organizationId || memberInfo?.activeOrganizationId || 1,
        ...(ngm ? { ngmId: memberInfo.id } : { userId: memberInfo.userId }),
        foodBoxType: null,
        optIn: true,
        optInType: selectedFoodBoxType,
        optInName: selectedOption?.label || '',
      };

      await authorizedApi.put(`${process.env.API_BASE_URL}/v1/foodbox/optin`, payload);
      
      // Update local state
      setIsOptedIn(true);
      setOptInTypeInfo({
        type: selectedFoodBoxType,
        name: selectedOption?.label || '',
      });
      
      setActions({ ...actions, optInUpdated: true, foodBoxTypeUpdated: true });
    } catch (error) {
      alert('Failed to save food box opt-in. Please try again.');
    } finally {
      setSubmittingOptIn(false);
    }
  };

  const handleCloseCareGap = async (careGapId, appointmentDate = null, appointmentTime = null) => {
    try {
      setSavingCareGap(careGapId);
      const authorizedApi = await getAuthClient();
      
      const payload = {
        organizationId: memberInfo?.organizationId || memberInfo?.activeOrganizationId ||  1,
        networkGroupMemberId: ngm ? memberInfo?.id : memberInfo?.networkGroupMemberId,
        careGapCode: careGapId,
        isClosed: true,
        appointmentDate: appointmentDate || null,
        appointmentTime: appointmentTime || null,
        closedByUserId: currentUserId || 0,
      };

      await authorizedApi.put(`${process.env.API_BASE_URL}/v1/foodbox/caregaps`, payload);

      // Refresh care gaps list to get updated data
      await fetchCareGaps();
      
      setActions({ ...actions, careGapClosed: true });
    } catch (error) {
      console.error('Failed to close care gap:', error);
      alert('Failed to close care gap. Please try again.');
    } finally {
      setSavingCareGap(null);
    }
  };

  const cancelSelected = () => {
    setActions({});
    if (setValues) {
      setValues({ ...values, currentTab: '', actionToDisplay: '' });
    }
    if (setActionToDisplay) {
      setActionToDisplay('');
    }
    if (setCurrentTab) {
      setCurrentTab('');
    }
  };

  return (
    <>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px 24px 10px' }}>
        <CancelButton onClick={cancelSelected} data-testid="CancelButton">
          Exit
        </CancelButton>
      </div>
      <StyledContainer style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
        <AddressSection style={{ flex: '1 1 50%', minWidth: 0 }}>
          <SectionTitle>Delivery Address Verification</SectionTitle>

          {submitSuccess && <SuccessMessage>Address verified and updated successfully!</SuccessMessage>}
          {submitError && !showAddressSuggestion && <ErrorMessage>{submitError}</ErrorMessage>}

          {showAddressSuggestion && verificationResult && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                border: verificationResult.isVerified ? '1px solid #6ee7b7' : '1px solid #fca5a5',
                backgroundColor: verificationResult.isVerified ? '#d1fae5' : '#fee2e2',
                color: verificationResult.isVerified ? '#059669' : '#dc2626',
              }}
            >
              {verificationResult.isVerified &&
                '✓ Address verified successfully! FedEx suggests the following formatted address.'}
              {!verificationResult.isVerified &&
                '⚠️ Address is recognized but not verified as a delivery point by FedEx. It might still be deliverable. Please review the suggested address below.'}
            </div>
          )}

          <Form>
            <StyledFormGroup>
              <StyledLabel htmlFor="streetAddress">Street Address</StyledLabel>
              <StyledInput
                id="streetAddress"
                type="text"
                placeholder={memberInfo?.address || 'Enter street address'}
                {...register('streetAddress', {
                  required: 'Street address is required',
                  minLength: {
                    value: 5,
                    message: 'Street address must be at least 5 characters',
                  },
                })}
              />
              {errors.streetAddress && <ErrorText>{errors.streetAddress.message}</ErrorText>}
            </StyledFormGroup>

            <StyledFormGroup>
              <StyledLabel htmlFor="city">City</StyledLabel>
              <StyledInput
                id="city"
                type="text"
                placeholder={memberInfo?.city || 'Enter city'}
                {...register('city', {
                  required: 'City is required',
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: 'City should only contain letters and spaces',
                  },
                })}
              />
              {errors.city && <ErrorText>{errors.city.message}</ErrorText>}
            </StyledFormGroup>

            <StyledFormGroup>
              <StyledLabel htmlFor="state">State</StyledLabel>
              <StyledInput
                id="state"
                type="text"
                placeholder={memberInfo?.state || 'Enter state'}
                {...register('state', {
                  required: 'State is required',
                  pattern: {
                    value: /^[A-Z]{2}$/,
                    message: 'State should be a 2-letter abbreviation (e.g., AZ)',
                  },
                })}
              />
              {errors.state && <ErrorText>{errors.state.message}</ErrorText>}
            </StyledFormGroup>

            <StyledFormGroup>
              <StyledLabel htmlFor="zipCode">ZIP Code</StyledLabel>
              <StyledInput
                id="zipCode"
                type="text"
                placeholder={memberInfo?.zip || 'Enter ZIP code'}
                {...register('zipCode', {
                  required: 'ZIP code is required',
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: 'ZIP code should be 5 digits or 9 digits (e.g., 12345 or 12345-6789)',
                  },
                })}
              />
              {errors.zipCode && <ErrorText>{errors.zipCode.message}</ErrorText>}
            </StyledFormGroup>

            {!showAddressSuggestion && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <StyledLoadingButton
                  onClick={handleSubmit(handleVerifyAddress)}
                  loading={isSubmitting}
                  variant="outlined"
                  style={{ flex: 1 }}
                >
                  Verify with FedEx
                </StyledLoadingButton>
                <StyledLoadingButton
                  onClick={handleSubmit(handleSaveAddress)}
                  loading={isSubmitting}
                  variant="contained"
                  style={{ flex: 1 }}
                >
                  Save Address
                </StyledLoadingButton>
              </div>
            )}
          </Form>

          {showAddressSuggestion && verificationResult?.suggestedAddress && (
            <div
              style={{
                marginTop: '20px',
                padding: '20px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}
            >
              <div
                style={{
                  backgroundColor: '#ffffff',
                  padding: '15px',
                  borderRadius: '4px',
                  marginBottom: '15px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                  <strong>{verificationResult.suggestedAddress.streetAddress}</strong>
                  <br />
                  {verificationResult.suggestedAddress.city}, {verificationResult.suggestedAddress.state}{' '}
                  {verificationResult.suggestedAddress.zipCode}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <StyledLoadingButton
                  onClick={(e) => {
                    e.preventDefault();
                    // Fill form with suggested address
                    reset({
                      streetAddress: verificationResult.suggestedAddress.streetAddress,
                      city: verificationResult.suggestedAddress.city,
                      state: verificationResult.suggestedAddress.state,
                      zipCode: verificationResult.suggestedAddress.zipCode,
                    });
                    setShowAddressSuggestion(false);
                    setSubmitError(null);
                  }}
                  variant="contained"
                  style={{ backgroundColor: '#2196f3', flex: '1', minWidth: '200px' }}
                >
                  Use This Address
                </StyledLoadingButton>
                <StyledLoadingButton
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAddressSuggestion(false);
                    setSubmitError(null);
                  }}
                  variant="outlined"
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  Keep Original
                </StyledLoadingButton>
              </div>
            </div>
          )}
        </AddressSection>

        <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
          <OptInSection style={{ gridColumn: 'auto' }}>
            <SectionTitle>Food Box Opt-In</SectionTitle>
            {loadingOptInStatus ? (
              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '10px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                Loading opt-in status...
              </div>
            ) : (
              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '10px',
                }}
              >
                {/* Show checkbox only for non-special groups or already opted-in members */}
                {!showDropdown && (
                  <>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                      }}
                    >
                      <OptInCheckbox
                        type="checkbox"
                        checked={isOptedIn}
                        onChange={handleOptInToggle}
                        disabled={submittingOptIn}
                        style={{ marginRight: '12px', width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <span>Opt-in to Food Box Program</span>
                    </label>
                    <p style={{ marginTop: '12px', fontSize: '14px', color: '#666', marginLeft: '32px' }}>
                      Check this box to participate in the food box delivery program.
                    </p>
                  </>
                )}

                {/* Show opt-in info for opted-in members */}
                {isOptedIn && optInTypeInfo.name && (
                  <div
                    style={{
                      marginTop: showDropdown ? '0' : '12px',
                      marginLeft: showDropdown ? '0' : '32px',
                      padding: '12px',
                      backgroundColor: '#d1fae5',
                      border: '1px solid #6ee7b7',
                      borderRadius: '6px',
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#059669', marginBottom: '4px' }}>
                      ✓ Opted In
                    </div>
                    <div style={{ fontSize: '13px', color: '#047857' }}>
                      Food Box Type: {optInTypeInfo.name}
                    </div>
                  </div>
                )}

                {/* Show dropdown for special group members who aren't opted in */}
                {showDropdown && (
                  <div style={{ marginTop: '0' }}>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                      Select a food box type to enroll in the delivery program.
                    </p>
                    <StyledLabel htmlFor="foodBoxType">Food Box Type</StyledLabel>
                    <select
                      id="foodBoxType"
                      value={selectedFoodBoxType}
                      onChange={handleFoodBoxTypeChange}
                      disabled={submittingOptIn}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        marginTop: '8px',
                      }}
                    >
                      {foodBoxOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Select the type of food box you would like to receive.
                    </p>
                    <StyledLoadingButton
                      onClick={handleSubmitOptIn}
                      loading={submittingOptIn}
                      variant="contained"
                      style={{
                        marginTop: '16px',
                        width: '100%',
                        backgroundColor: '#059669',
                      }}
                    >
                      Submit Food Box Selection
                    </StyledLoadingButton>
                  </div>
                )}
              </div>
            )}
          </OptInSection>

          <CareGapsSection>
            <SectionTitle>Care Gaps</SectionTitle>
            {loadingCareGaps ? (
              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '10px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                Loading care gaps...
              </div>
            ) : careGaps.length === 0 ? (
              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '10px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                No care gaps found
              </div>
            ) : (
              <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {careGaps.map((gap) => (
                  <CareGapItem
                    key={gap.id}
                    gap={gap}
                    onClose={handleCloseCareGap}
                    isSaving={savingCareGap === gap.id}
                  />
                ))}
              </div>
            )}
          </CareGapsSection>
        </div>
      </StyledContainer>
    </>
  );
};

const CareGapItem = ({ gap, onClose, isSaving }) => {
  const [showAppointmentInput, setShowAppointmentInput] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');

  const handleClose = () => {
    onClose(gap.id, appointmentDate || null, appointmentTime || null);
    setShowAppointmentInput(false);
    setAppointmentDate('');
    setAppointmentTime('');
  };

  // Convert date from YYYY-MM-DD to MM/DD/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  };

  // Convert 24-hour time to 12-hour format (e.g., "14:30" -> "2:30 PM")
  const formatTime12Hour = (time24) => {
    if (!time24) return null;
    
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12 for midnight
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (gap.isClosed) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#d1fae5',
          border: '1px solid #6ee7b7',
          borderRadius: '6px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669', marginBottom: '8px' }}>
              ✓ {gap.name}
            </div>
            <div style={{ fontSize: '13px', color: '#047857' }}>
              <div>Closed: {formatDate(gap.closureDate)}</div>
              {gap.closedBy && <div>Closed by: {gap.closedBy}</div>}
              {gap.appointmentDate && (
                <div>
                  Appointment: {formatDate(gap.appointmentDate)}
                  {gap.appointmentTime && ` at ${formatTime12Hour(gap.appointmentTime)}`}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              backgroundColor: '#059669',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            CLOSED
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: '6px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#92400e' }}>{gap.name}</div>
        <div
          style={{
            backgroundColor: '#f59e0b',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          OPEN
        </div>
      </div>

      {!showAppointmentInput ? (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleClose}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
            }}
          >
            {isSaving ? 'Closing...' : 'Close Care Gap'}
          </button>
          <button
            onClick={() => setShowAppointmentInput(true)}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
            }}
          >
            Add Appointment
          </button>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '8px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#92400e',
                marginBottom: '4px',
              }}
            >
              Appointment Date (Optional)
            </label>
            <input
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#92400e',
                marginBottom: '4px',
              }}
            >
              Appointment Time (Optional)
            </label>
            <input
              type="time"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleClose}
              disabled={isSaving}
              style={{
                padding: '8px 16px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                flex: 1,
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              {isSaving ? 'Closing...' : 'Close with Appointment'}
            </button>
            <button
              onClick={() => {
                setShowAppointmentInput(false);
                setAppointmentDate('');
                setAppointmentTime('');
              }}
              disabled={isSaving}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CareGapItem.propTypes = {
  gap: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isClosed: PropTypes.bool.isRequired,
    closureDate: PropTypes.string,
    appointmentDate: PropTypes.string,
    appointmentTime: PropTypes.string,
    closedBy: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

FoodBoxActivity.propTypes = {
  ngm: PropTypes.bool.isRequired,
  memberInfo: PropTypes.shape({
    id: PropTypes.number,
    ngmId: PropTypes.number,
    userId: PropTypes.number,
    organizationId: PropTypes.number,
    groupId: PropTypes.number,
    networkId: PropTypes.number,
    networkGroupMemberId: PropTypes.number,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }).isRequired,
  setActions: PropTypes.func.isRequired,
  actions: PropTypes.object,
  setCurrentTab: PropTypes.func,
  setActionToDisplay: PropTypes.func,
  values: PropTypes.object,
  setValues: PropTypes.func,
};

export default FoodBoxActivity;
